import { DateTime } from "luxon";

/**
 * Formats a given JavaScript Date to specific string formats: NUMERIC_DATE_FORMAT, RELATIVE_DATE_FORMAT, STANDARD_DATE_FORMAT, and TIME_AGO_FORMAT.
 *
 * NUMERIC_DATE_FORMAT: Always formats to "M/d/yy",
 *                      if include_time is true, it appends the time (e.g., "M/d/yy, h:mm a").
 *                       - "8/1/24"
 *                       - "8/1/24, 3:00 PM" (if include_time is true)
 *                       - "12/25/24, 10:30 AM"
 * RELATIVE_DATE_FORMAT: Formats to "today", "tomorrow", or "yesterday" with time (e.g., "today, 3:00 PM").
 *                       Falls back to STANDARD_DATE_FORMAT if the date is not today, tomorrow, or yesterday.
 *                       - "today, 3:00 PM"
 *                       - "tomorrow, 3:00 PM"
 *                       - "yesterday, 3:00 PM"
 *                       - "July 4, 3:00 PM"
 *                       - "July 4, 2023, 3:00 PM"
 * STANDARD_DATE_FORMAT: Formats to "LLL d", appends the year only if the date is not the current year,
 *                       if include_time is true, it appends the time (e.g., "LLL d yyyy, h:mm a").
 *                       - "July 4"
 *                       - "July 4, 2023"
 *                       - "July 4, 3:00 PM"
 *                       - "July 4, 2023, 3:00 PM"
 * TIME_AGO_FORMAT: Formats to a human-readable relative time string (e.g., "5m ago", "2h ago", "3d ago").
 *                  Uses seconds, minutes, hours, days, months, and years as needed.
 *                  - "20s ago"
 *                  - "5m ago"
 *                  - "3h ago"
 *                  - "2d ago"
 *                  - "3mo ago"
 *                  - "1y ago"
 *
 * @param  date - The JavaScript Date to be formatted.
 * @param  [options] - Formatting options.
 * @param  [options.include_time=false] - Whether to include the hour in the output.
 * @param  [options.include_year=false] - Whether to include the year in the output - only for `STANDARD_DATE_FORMAT`.
 * @returns An object containing formatted date strings.
 * @returns formatJSDate.NUMERIC_DATE_FORMAT = The formatted date in "M/d/yy", or "M/d/yy, h:mm a" if include_time is true.
 * @returns formatJSDate.RELATIVE_DATE_FORMAT = The formatted relative date string, or relative date with time if include_time is true.
 *                                              If the date is not today, tomorrow, or yesterday, it falls back to STANDARD_DATE_FORMAT.
 * @returns formatJSDate.STANDARD_DATE_FORMAT = The formatted date in "LLL d", or "LLL d yyyy" if the year is not current, or "LLL d, h:mm a" if include_time is true.
 * @returns formatJSDate.TIME_AGO_FORMAT = A relative time string like "20s ago", "5m ago", "3h ago", "2d ago", "3mo ago", "1y ago".
 *
 * @example
 * const formattedDate = formatJSDate(new Date(), { include_time: true });
 * console.log(formattedDate.NUMERIC_DATE_FORMAT); // "M/d/yy, 3:00 PM"
 * console.log(formattedDate.RELATIVE_DATE_FORMAT); // "today, 3:00 PM"
 * console.log(formattedDate.STANDARD_DATE_FORMAT); // "LLL d, 3:00 PM" or "LLL d yyyy, 3:00 PM" if not the current year
 * console.log(formattedDate.TIME_AGO_FORMAT); // "5m ago", "3h ago", "2d ago"
 */
export function formatJSDate(
    date: Date,
    options?: { include_time?: boolean; include_year?: boolean },
) {
    const includeTime = options?.include_time ?? false;
    const includeYear = options?.include_year ?? false;
    const inputDate = DateTime.fromJSDate(date);
    const now = DateTime.local();
    const today = now.startOf("day");
    const tomorrow = today.plus({ days: 1 });
    const yesterday = today.minus({ days: 1 });

    // Format NUMERIC_DATE_FORMAT
    let NUMERIC_DATE_FORMAT = inputDate.toFormat("M/d/yy");

    // Determine the format for STANDARD_DATE_FORMAT
    const currentYear = today.year;
    let STANDARD_DATE_FORMAT = inputDate.toFormat(
        !includeYear ? (currentYear === inputDate.year ? "LLL d" : "LLL d yyyy") : "LLL d yyyy",
    );

    // Determine RELATIVE_DATE_FORMAT
    let RELATIVE_DATE_FORMAT: string;

    if (inputDate.hasSame(today, "day")) {
        RELATIVE_DATE_FORMAT = "today";
    } else if (inputDate.hasSame(tomorrow, "day")) {
        RELATIVE_DATE_FORMAT = "tomorrow";
    } else if (inputDate.hasSame(yesterday, "day")) {
        RELATIVE_DATE_FORMAT = "yesterday";
    } else {
        // If the date is not today, tomorrow, or yesterday, RELATIVE_DATE_FORMAT falls back to STANDARD_DATE_FORMAT
        RELATIVE_DATE_FORMAT = STANDARD_DATE_FORMAT;
    }

    // Append time if include_time is true
    if (includeTime) {
        const hourFormat = "h:mm a";
        const timeString = inputDate.toFormat(hourFormat);
        RELATIVE_DATE_FORMAT += `, ${timeString}`;
        NUMERIC_DATE_FORMAT += `, ${timeString}`;
        STANDARD_DATE_FORMAT += `, ${timeString}`;
    }

    // Determine TIME_AGO_FORMAT
    const diff = now
        .diff(inputDate, ["seconds", "minutes", "hours", "days", "months", "years"])
        .toObject();
    let TIME_AGO_FORMAT: string;

    if (diff.years && diff.years >= 1) {
        TIME_AGO_FORMAT = `${Math.round(diff.years)}y ago`;
    } else if (diff.months && diff.months >= 1) {
        TIME_AGO_FORMAT = `${Math.round(diff.months)}mo ago`;
    } else if (diff.days && diff.days >= 1) {
        TIME_AGO_FORMAT = `${Math.round(diff.days)}d ago`;
    } else if (diff.hours && diff.hours >= 1) {
        TIME_AGO_FORMAT = `${Math.round(diff.hours)}h ago`;
    } else if (diff.minutes && diff.minutes >= 1) {
        TIME_AGO_FORMAT = `${Math.round(diff.minutes)}m ago`;
    } else {
        TIME_AGO_FORMAT = `${Math.round(diff.seconds ?? 0)}s ago`;
    }

    return {
        /**
         * The formatted date in "M/d/yy", or "M/d/yy, h:mm a" if include_time is true.
         * @example "8/1/24", "8/1/24, 3:00 PM", "12/25/23, 10:30 AM"
         */
        NUMERIC_DATE_FORMAT,
        /**
         * The formatted relative date string, or relative date with time if include_time is true.
         * If the date is not today, tomorrow, or yesterday, it falls back to STANDARD_DATE_FORMAT.
         * @example "today, 3:00 PM", "tomorrow, 3:00 PM", "yesterday, 3:00 PM", "July 4, 2023, 3:00 PM"
         */
        RELATIVE_DATE_FORMAT,
        /**
         * The formatted date in "LLL d", or "LLL d yyyy" if the year is not current, or "LLL d, h:mm a" if include_time is true.
         * @example "July 4", "July 4, 2024", "July 4, 3:00 PM", "July 4, 2023, 3:00 PM"
         */
        STANDARD_DATE_FORMAT,
        /**
         * A relative time string like "20s ago", "5m ago", "3h ago", "2d ago", "3mo ago", "1y ago".
         * @example "20s ago", "5m ago", "3h ago", "2d ago"
         */
        TIME_AGO_FORMAT,
    };
}

/**
 * Presets for Custom Date Range Picker.
 */
export const DATERANGE_SHORTCUTS: Record<string, [number, number] | null> = {
    Today: [DateTime.now().startOf("day").toMillis(), DateTime.now().endOf("day").toMillis()],
    "This Week": [
        DateTime.now().startOf("week").toMillis(),
        DateTime.now().endOf("week").toMillis(),
    ],
    "Last Week": [
        DateTime.now().minus({ weeks: 1 }).startOf("week").toMillis(),
        DateTime.now().minus({ weeks: 1 }).endOf("week").toMillis(),
    ],
    "This Month": [
        DateTime.now().startOf("month").toMillis(),
        DateTime.now().endOf("month").toMillis(),
    ],
    "Last Month": [
        DateTime.now().minus({ months: 1 }).startOf("month").toMillis(),
        DateTime.now().minus({ months: 1 }).endOf("month").toMillis(),
    ],
    "All Time": null,
};

/**
 * Formats date as HH:MM AM/PM from a Date or string.
 */
export function formatTime(date: Date | string): string {
    const newDate = new Date(date);
    const hours = newDate.getHours();
    const minutes = String(newDate.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes} ${ampm}`;
}

/**
 * Formats a Date object into a string representing the time in 12-hour format.
 *
 * @param  date The Date object to format.
 * @returns  A formatted string representing the time in HH:MM format (e.g., "9:30 AM").
 */
export function toHHMM(date: Date): string {
    const mins = date.getMinutes();
    const hours = date.getHours() % 12 || 12;

    const amOrPm = date.getHours() < 12 ? "AM" : "PM";

    return `${hours}:${String(mins).padStart(2, "0")} ${amOrPm}`;
}
